import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  provideZoneChangeDetection,
  importProvidersFrom,
  provideAppInitializer,
  inject,
  ErrorHandler,
  LOCALE_ID,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { enableMapSet } from 'immer';
import { NgxPermissionsModule } from 'ngx-permissions';
import { FeatureFlagService, UserService } from 'shared/domain';
import { NodeDetailPopoverComponent, NodeDetailPopoverReferencesComponent } from 'shared/feature-node-detail-popover';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { authHttpInterceptor, AuthService, AuthStorageService } from '@treetop/authentication';
import { NODE_DETAIL_POPOVER_COMPONENT, NODE_DETAIL_POPOVER_REFERENCES_DATA } from '@treetop/shared/model';

import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { ErrorFactory } from './app/ErrorFactory';
import { TranslocoHttpLoaderService } from './app/transloco-http-loader.service';
import { Environment } from './environments/environment';
import { GuidelineSyncService } from '../../../libs/shared/util-common/src/lib/services/guideline-sync.service';
import { NetworkStatusService } from '../../../libs/shared/util-common/src/lib/services/network-status.service';

enableMapSet();

export const treetopAppInitializer = async () => {
  const network = inject(NetworkStatusService);
  const featureFlags = inject(FeatureFlagService);
  const auth = inject(AuthService);
  const userService = inject(UserService);
  const guidelineSyncService = inject(GuidelineSyncService);

  const isOnline = await network.init();

  const tasks: Promise<unknown>[] = [guidelineSyncService.init()];

  if (isOnline) {
    tasks.push(featureFlags.init(), auth.init());
  } else {
    userService.initUser();
  }

  await Promise.all(tasks);
};

TreetopPropertiesProvider.init({
  baseUrl: Environment.ApiBackendBaseUrl,
  version: Environment.Version,
  production: Environment.production,
  publicLinkBaseUrl: Environment.PublicLinkBaseUrl,
  workflowFrontendBaseUrl: Environment.WorkflowFrontendBaseUrl,
  hubUrl: Environment.HubUrl,
  enableOffline: Environment.EnableOffline,
  insights: {
    endpoint: Environment.ApplicationInsightsEndpoint,
    connectionString: Environment.ApplicationInsightsConnectionString,
  },
  authB2C: {
    defaultScopeName: Environment.AuthB2C?.defaultScopeName,
    tenant: Environment.AuthB2C?.tenant,
    tenantId: Environment.AuthB2C?.tenantId,
    clientId: Environment.AuthB2C?.clientId,
    instance: Environment.AuthB2C?.instance,
    signupsigninflow: Environment.AuthB2C?.signupsigninflow,
  },
});

if (TreetopPropertiesProvider.properties.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(NgxPermissionsModule.forRoot(), MatDialogModule, MatIconModule, MatSnackBarModule),
    provideZoneChangeDetection(),
    provideServiceWorker('custom-sw.js', {
      enabled: TreetopPropertiesProvider.properties.enableOffline,
      registrationStrategy: 'registerWhenStable:15000',
    }),
    provideRouter(APP_ROUTES),
    provideAnimations(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'de'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: TreetopPropertiesProvider.properties.production,
      },
      loader: TranslocoHttpLoaderService,
    }),
    provideHttpClient(withInterceptors([authHttpInterceptor])),
    provideOAuthClient(),
    { provide: OAuthStorage, useClass: AuthStorageService },
    provideAppInitializer(treetopAppInitializer),
    {
      provide: ErrorHandler,
      useFactory: ErrorFactory,
    },
    provideTranslocoLocale({
      langToLocaleMapping: {
        en: 'en-US',
        de: 'de-AT',
      },
      localeConfig: {
        global: {
          date: { day: '2-digit', month: '2-digit', year: 'numeric' },
        },
      },
    }),
    { provide: MAT_DATE_LOCALE, useValue: 'de' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: LOCALE_ID, useValue: 'de-AT' },
    { provide: NODE_DETAIL_POPOVER_REFERENCES_DATA, useValue: NodeDetailPopoverReferencesComponent },
    { provide: NODE_DETAIL_POPOVER_COMPONENT, useValue: NodeDetailPopoverComponent },
  ],
}).catch((err) => console.error(err));
